import { apiGet, apiPut, apiDelete } from './client';

/**
 * Phase 71 (SBH-01) → Phase 84 — HR-only per-user/month salary client,
 * matching backend/src/api/routes/adminEmployeeSalary.ts (requireHR-gated).
 *
 * Phase 84 records three amounts — net, gross and super gross cost
 * (including odvody) — plus `deductedField`, which names the one the
 * freelancer invoice subtracts. The choice lives on the month because it
 * genuinely varies per person: for some people the gross is deducted, for
 * others the net.
 *
 * All three amounts null clears the month (an unentered month = 0 deduction).
 */

/**
 * `none` — the salary is recorded for cost reporting but does NOT reduce the
 * freelancer invoice. Some people draw a salary for their employment AND bill
 * every contractor hour at a rate on top; those are independent streams.
 */
export type DeductedField = 'net' | 'gross' | 'total_cost' | 'none';

export const DEDUCTED_FIELD_LABELS: Record<DeductedField, string> = {
  net: 'Net salary',
  gross: 'Gross salary',
  total_cost: 'Super gross cost',
  none: 'No deduction',
};

export interface EmployeeSalary {
  userId: number;
  year: number;
  month: number;
  netAmount: number | null;
  grossAmount: number | null;
  totalCostAmount: number | null;
  deductedField: DeductedField;
  /** The resolved figure the invoice deducts — server-computed, do not re-derive. */
  deductedAmount: number | null;
  updatedAt: string;
}

export interface EmployeeSalaryInput {
  netAmount: number | null;
  grossAmount: number | null;
  totalCostAmount: number | null;
  deductedField: DeductedField;
}

/** One entry in the edit trail (D-03). `changes` is per-state, hence unknown. */
export interface EmployeeSalaryHistoryEntry {
  id: number;
  state: 'created' | 'updated' | 'deleted' | string;
  userId: number | null;
  createdAt: string;
  changes: {
    year?: number;
    month?: number;
    invoiceState?: string | null;
    /** True when the month's invoice was already past `open` at edit time. */
    editedClosedMonth?: boolean;
    fields?: Record<string, unknown>;
    before?: Record<string, unknown>;
    deductedField?: string;
    [key: string]: unknown;
  };
}

function base(userId: number, year: number, month: number): string {
  return `/admin/employee-invoices/${userId}/${year}/${month}/salary`;
}

export async function getEmployeeSalary(
  userId: number,
  year: number,
  month: number
): Promise<EmployeeSalary | null> {
  const res = await apiGet<{ data: EmployeeSalary | null }>(base(userId, year, month));
  return res.data;
}

export async function setEmployeeSalary(
  userId: number,
  year: number,
  month: number,
  input: EmployeeSalaryInput
): Promise<EmployeeSalary | null> {
  const res = await apiPut<{ data: EmployeeSalary | null }>(base(userId, year, month), input);
  return res.data;
}

export async function getEmployeeSalaryHistory(
  userId: number,
  year: number,
  month: number
): Promise<EmployeeSalaryHistoryEntry[]> {
  const res = await apiGet<{ data: EmployeeSalaryHistoryEntry[] }>(
    `${base(userId, year, month)}/history`
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Bulk monthly entry
// ---------------------------------------------------------------------------

export interface BulkSalaryRow {
  userId: number;
  userName: string;
  /** Already recorded for the selected month, if anything. */
  current: EmployeeSalary | null;
  /** The previous month — what the table prefills from. */
  previous: EmployeeSalary | null;
  /**
   * False for someone who started this month: nothing to copy forward. They are
   * listed separately rather than hidden, so a first salary cannot be forgotten.
   */
  hadPreviousMonth: boolean;
  /**
   * True only when the person also holds an active contractor-category
   * relationship that month — the only case where there is a freelancer
   * invoice to deduct the salary from.
   */
  hasConcurrentEmployment: boolean;
}

export interface BulkSalaryEntry extends EmployeeSalaryInput {
  userId: number;
}

export interface BulkSalaryResult {
  saved: number;
  cleared: number;
  failed: Array<{ userId: number; message: string }>;
}

export async function getBulkSalaryRoster(
  year: number,
  month: number
): Promise<BulkSalaryRow[]> {
  const res = await apiGet<{ data: BulkSalaryRow[] }>(
    `/admin/employee-invoices/salaries/${year}/${month}`
  );
  return res.data;
}

export async function saveBulkSalaries(
  year: number,
  month: number,
  entries: BulkSalaryEntry[]
): Promise<BulkSalaryResult> {
  const res = await apiPut<{ data: BulkSalaryResult }>(
    `/admin/employee-invoices/salaries/${year}/${month}`,
    { entries }
  );
  return res.data;
}

export async function clearEmployeeSalary(
  userId: number,
  year: number,
  month: number
): Promise<void> {
  await apiDelete(base(userId, year, month));
}
